import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { SubscriptionModal } from "./SubscriptionModal";
import { CreditPurchaseModal } from "./CreditPurchaseModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sparkles, Crown, Zap, Settings, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";

const TIER_ICONS: Record<string, React.ReactNode> = {
  basic: <Zap className="h-3 w-3" />,
  pro: <Sparkles className="h-3 w-3" />,
  premium: <Crown className="h-3 w-3" />,
};

const TIER_COLORS: Record<string, string> = {
  basic: "bg-blue-500/10 text-blue-500",
  pro: "bg-purple-500/10 text-purple-500",
  premium: "bg-amber-500/10 text-amber-500",
};

export function UserCreditsStatus() {
  const { hasActiveSubscription, subscription, isLoading: subscriptionLoading } = useSubscription();
  const [credits, setCredits] = useState<number | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const { t } = useTranslation();

  const fetchCredits = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setCredits(null);
        return;
      }
      const { data, error } = await supabase.functions.invoke("check-credits", {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      if (error) throw error;
      setCredits(data.balance);
    } catch (error) {
      console.error("Error fetching credits:", error);
      setCredits(null);
    }
  };

  useEffect(() => {
    fetchCredits();
    
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        fetchCredits();
      } else {
        setCredits(null);
      }
    });

    const creditChannel = supabase
      .channel('user-credit-updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_credits' },
        () => fetchCredits()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'credit_transactions' },
        () => fetchCredits()
      )
      .subscribe();

    const interval = setInterval(() => {
      if (credits !== null) fetchCredits();
    }, 30000);

    return () => {
      authSub.unsubscribe();
      supabase.removeChannel(creditChannel);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleCreditUpdate = () => fetchCredits();
    window.addEventListener('creditUpdate', handleCreditUpdate);
    return () => window.removeEventListener('creditUpdate', handleCreditUpdate);
  }, []);

  const handlePurchaseComplete = () => {
    fetchCredits();
    setShowPurchaseModal(false);
  };

  if (subscriptionLoading || credits === null) {
    return null;
  }

  // Not subscribed - show subscribe button with credits inside
  if (!hasActiveSubscription) {
    return (
      <>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowSubscriptionModal(true)}
          className="gap-1.5 text-xs px-2.5 py-1.5 h-auto"
        >
          <Sparkles className="h-3 w-3" />
          <span>{t("subscription.subscribe", "Subscribe")}</span>
          {credits > 0 && (
            <>
              <span className="text-muted-foreground">•</span>
              <span>{credits.toLocaleString()}</span>
            </>
          )}
        </Button>
        <SubscriptionModal open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal} />
      </>
    );
  }

  // Subscribed - show dropdown with plan management and credits
  const tier = subscription?.planTier || "basic";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div 
            className="flex items-center gap-2 px-3 py-1.5 bg-secondary/20 rounded-full border cursor-pointer hover:bg-secondary/30 transition-colors"
          >
            <Badge 
              variant="outline" 
              className={`gap-1 ${TIER_COLORS[tier]} border-0 hidden sm:flex`}
            >
              {TIER_ICONS[tier]}
              {subscription?.planName}
            </Badge>
            
            <span className="text-muted-foreground hidden sm:inline">•</span>
            
            <span className={`text-sm font-medium ${credits === 0 ? 'text-primary' : ''}`}>
              {credits === 0 
                ? t("credits.getCredits", "0 Credits - Get more")
                : credits.toLocaleString()
              }
            </span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => setShowSubscriptionModal(true)}>
            <Settings className="h-4 w-4 mr-2" />
            {t("subscription.managePlan", "Manage Plan")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowPurchaseModal(true)}>
            <Coins className="h-4 w-4 mr-2" />
            {t("navigation.buyCredits", "Buy Credits")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <SubscriptionModal open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal} />
      <CreditPurchaseModal 
        open={showPurchaseModal}
        onOpenChange={setShowPurchaseModal}
        onPurchaseComplete={handlePurchaseComplete}
      />
    </>
  );
}
